import React from 'react';
import { Card, CardContent, Box, Typography, Grid } from '@mui/material';
import PropTypes from 'prop-types';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

const CardTableFinanceiro = (props) => {
  const { className, valorEmAberto, title, valorPago, valorTotal, textValorTotal, ...rest } = props;

  const isEmpenho = title?.toUpperCase().includes('EMPENHO');
  const accentColor = isEmpenho ? '#64748b' : '#475569';

  return (
    <Card
      {...rest}
      variant="outlined"
      className={className}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        borderLeft: `6px solid ${accentColor}`,
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
        },
        width: '100%',
        ...rest.sx
      }}
    >
      {/* Header bar */}
      <Box
        sx={{
          bgcolor: '#f8fafc',
          px: 2.5,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        {isEmpenho ? (
          <AssignmentIcon sx={{ color: accentColor }} />
        ) : (
          <AccountBalanceWalletIcon sx={{ color: accentColor }} />
        )}
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 'bold',
            color: '#1e293b',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
          }}
        >
          {title}
        </Typography>
      </Box>

      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Grid container spacing={2}>
          {/* Valor Pago */}
          <Grid item xs={6}>
            <Box
              sx={{
                p: 2,
                borderRadius: 1.5,
                bgcolor: '#e8f5e9',
                border: '1px solid #c8e6c9',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CheckCircleOutlineIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
                <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 'bold', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                  Valor Pago
                </Typography>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: '#1b5e20',
                  wordBreak: 'break-all',
                }}
              >
                {valorPago}
              </Typography>
            </Box>
          </Grid>

          {/* Valor em Aberto */}
          <Grid item xs={6}>
            <Box
              sx={{
                p: 2,
                borderRadius: 1.5,
                bgcolor: '#fde8e8',
                border: '1px solid #f8b4b4',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ErrorOutlineIcon sx={{ color: '#c53030', fontSize: 20 }} />
                <Typography variant="caption" sx={{ color: '#c53030', fontWeight: 'bold', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                  Valor em Aberto
                </Typography>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: '#9b1c1c',
                  wordBreak: 'break-all',
                }}
              >
                {valorEmAberto}
              </Typography>
            </Box>
          </Grid>

          {/* Valor Total Banner */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                borderRadius: 1.5,
                bgcolor: '#f1f5f9',
                border: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MonetizationOnIcon sx={{ color: '#475569', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600 }}>
                  {textValorTotal}
                </Typography>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: '#1e293b',
                }}
              >
                {valorTotal}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

CardTableFinanceiro.propTypes = {
  title: PropTypes.string,
  valorEmAberto: PropTypes.string,
  valorPago: PropTypes.string,
  valorTotal: PropTypes.string,
  textValorTotal: PropTypes.string,
};

export default CardTableFinanceiro;
